/**
 * The browsing panel.
 *
 * A side panel on a wide screen, a bottom sheet on touch — where it can also
 * be swiped away by its grip.
 */

import { type Component, createSignal, Show } from 'solid-js';
import { closeSheet, sheet } from '../../state/ui.ts';
import { Close } from '../icons/nav.tsx';
import glass from '../primitives/Glass.module.css';
import { GlassButton } from '../primitives/Glass.tsx';
import { SearchField } from './SearchField.tsx';
import styles from './Sheet.module.css';
import { SheetBody } from './SheetBody.tsx';

/** Pixels of downward drag after which the sheet dismisses itself. */
const DISMISS_AFTER = 90;
const DESKTOP = '(min-width: 901px)';

export const Sheet: Component = () => {
  let grip!: HTMLDivElement;
  const [dragOffset, setDragOffset] = createSignal<number | null>(null);
  let startY = 0;

  const onGripDown = (e: PointerEvent): void => {
    if (matchMedia(DESKTOP).matches) return;
    startY = e.clientY;
    setDragOffset(0);
    /* Capture on the grip, which carries these handlers: capturing on the
       panel would retarget every later move and release to the panel, past
       them, and strand the sheet mid-drag with its transition switched off. */
    grip.setPointerCapture(e.pointerId);
  };

  const onGripMove = (e: PointerEvent): void => {
    if (dragOffset() === null) return;
    setDragOffset(Math.max(0, e.clientY - startY));
  };

  /* Also the lost-capture handler, so an interrupted drag still lets go. */
  const onGripUp = (): void => {
    const offset = dragOffset();
    setDragOffset(null);
    if (offset !== null && offset > DISMISS_AFTER) closeSheet();
  };

  const transform = (): string | undefined => {
    const offset = dragOffset();
    return offset === null ? undefined : `translateY(${offset}px)`;
  };

  return (
    <>
      <button
        type="button"
        aria-label="Dismiss"
        class={[styles.scrim, { [styles.scrimShown as string]: sheet().open }]}
        onClick={closeSheet}
      />

      <section
        data-testid="sheet"
        class={[glass.glass, styles.sheet, { [styles.shown as string]: sheet().open }]}
        style={{ transform: transform(), transition: dragOffset() === null ? undefined : 'none' }}
        aria-modal="true"
        role="dialog"
        aria-label={sheet().title}
      >
        <div
          ref={grip}
          class={styles.grip}
          data-testid="sheet-grip"
          onPointerDown={onGripDown}
          onPointerMove={onGripMove}
          onPointerUp={onGripUp}
          onPointerCancel={onGripUp}
          onLostPointerCapture={onGripUp}
        >
          <span />
        </div>

        <header class={styles.head}>
          <h2 data-testid="sheet-title">{sheet().title}</h2>
          <GlassButton small label="Close" onClick={closeSheet}>
            <Close />
          </GlassButton>
        </header>

        <Show when={sheet().view === 'search'}>
          <SearchField />
        </Show>

        <div class={styles.body} data-testid="sheet-body">
          <SheetBody />
        </div>
      </section>
    </>
  );
};
